/* eslint-disable react/no-this-in-sfc */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { GraphHeader, StyledGraph, StyledTag } from './Graph.styles';

const Graph = ({ graphData, isLoading, tooltipBg, title, amount, dashboardGraph, setSearchQuery, searchQuery, embedded }) => {
  const chartRef = useRef(null);
  const [chartObject, setChartObject] = useState();
  const [dropdown, setDropdown] = useState(false);
  const [visibleMonths, setVisibleMonths] = useState(12);
  const isToday = searchQuery?.dateRange === 'today';

  useEffect(() => {
    let xAxis = graphData?.xAxis?.slice(-visibleMonths) || [];

    let totalSendingData = graphData?.totalSending?.slice(-visibleMonths) || [];
    let connectionsAcceptedData = graphData?.connectionsAccepted?.slice(-visibleMonths) || [];
    let totalRepliesData = graphData?.totalReplies?.slice(-visibleMonths) || [];
    const leadsData = graphData?.leads?.slice(-visibleMonths) || [];

    const hasData = arr => Array.isArray(arr) && arr.some(v => Number(v) > 0);

    const noDashboardData =
      dashboardGraph &&
      !hasData(totalSendingData) &&
      !hasData(connectionsAcceptedData) &&
      !hasData(totalRepliesData);

    if (noDashboardData) {
      if (isToday) {
        xAxis = [''];
        totalSendingData = [0];
        connectionsAcceptedData = [0];
        totalRepliesData = [0];
      } else {
        const length = Math.max(xAxis.length || 1, 6);
        const flatLine = new Array(length).fill(0);

        if (xAxis.length === 0) {
          xAxis = new Array(length).fill('');
        } else if (xAxis.length < length) {
          xAxis = [...xAxis, ...new Array(length - xAxis.length).fill('')];
        } else if (xAxis.length > length) {
          xAxis = xAxis.slice(-length);
        }

        totalSendingData = flatLine;
        connectionsAcceptedData = flatLine;
        totalRepliesData = flatLine;
      }
    }

    const isColumnChart = isToday;

    const chartSeries = dashboardGraph
      ? [
        {
          name: 'Total Sending',
          data: totalSendingData,
          color: '#2783EC',
          fillColor: '#ECEFF1',
        },
        {
          name: 'Connection Accepted',
          data: connectionsAcceptedData,
          color: '#F6C534',
          fillColor: '#F1F1F4',
        },
        {
          name: 'Total Replies',
          data: totalRepliesData,
          color: '#01C638',
          fillColor: '#F9F9FB',
        },
      ]
      : [
        {
          name: 'Leads',
          data: leadsData,
          color: '#8B91B9',
          fillColor: '#F3F4F8',
        },
      ];

    setChartObject({
      title: { text: '' },
      chart: {
        type: isColumnChart ? 'column' : 'spline',
        spacingLeft: 0,
        marginLeft: 0,
        marginRight: 60,
        spacingBottom: 0,
      },
      xAxis: {
        categories: xAxis,
        crosshair: false,
        visible: true,
      },
      tooltip: {
        shared: false,
      },
      yAxis: {
        opposite: true,
        gridLineDashStyle: 'Solid',
        gridLineColor: 'rgba(157, 157, 157, 0.2)',
        labels: {
          formatter: function () {
            if (this.value >= 1000) {
              return this.value / 1000 + 'K';
            }
            return this.value;
          },
        },
      },
      series: chartSeries,
      plotOptions: {
        series: {
          animation: { duration: 1000 },
          marker: { enabled: false },
          lineWidth: 2,
          // For column charts we let Highcharts position points normally.
          // For line/spline charts we place points "on" categories to align with labels.
          pointPlacement: isColumnChart ? undefined : 'on',
        },
        column: {
          borderRadius: 4,
          pointPadding: 0.1,
          groupPadding: isToday && noDashboardData ? 0.35 : 0.1,
          maxPointWidth: 45,
          minPointLength: 3,
        },
      },
      responsive: {
        rules: [
          {
            condition: { maxWidth: 500 },
            chartOptions: {},
          },
        ],
      },
    });
  }, [graphData, dashboardGraph, visibleMonths, isToday]);

  return (
    <StyledGraph $embedded={embedded}>
      <GraphHeader bg={tooltipBg}>
        <ul className="title-list">
          <li>Total Sending</li>
          <li>Connection Accepted</li>
          <li>Total Replies</li>
        </ul>
        {dashboardGraph ? (
          <div className="dashboard-header">
            <StyledTag $tagcolor="#2783EC">
              <span>Total Sending</span>
            </StyledTag>
            <StyledTag $tagcolor="#F6C534">
              <span>Connection Accepted</span>
            </StyledTag>
            <StyledTag $tagcolor="#01C638">
              <span>Total Replies</span>
            </StyledTag>
          </div>
        ) : (
          <div>
            <span className="title">{title}</span>
            <strong className="leads">{graphData?.totalLeads} Leads</strong>
          </div>
        )}
      </GraphHeader>
      <div className="my-custom-chart">
        <HighchartsReact highcharts={Highcharts} options={chartObject} ref={chartRef} />
      </div>
    </StyledGraph>
  );
};

Graph.propTypes = {
  graphData: PropTypes.shape({
    xAxis: PropTypes.arrayOf(PropTypes.string).isRequired,
    income: PropTypes.arrayOf(PropTypes.number).isRequired,
    spending: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  tooltipBg: PropTypes.string,
  title: PropTypes.string,
  amount: PropTypes.number,
};

export default Graph;
